import { useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import type { FilmType } from "@/mocks/films";
import {
  getFilmsByType,
  typeLabels,
  typeDescriptions,
  allRegions,
  allYears,
} from "@/utils/catalog";
import FilmCard from "@/components/base/FilmCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FilterGroup from "@/pages/category/components/FilterGroup";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

const heatOptions = [
  { value: "hot", label: "热度高" },
  { value: "rated", label: "口碑佳" },
];

const sortOptions = [
  { value: "time", label: "按发布时间" },
  { value: "popularity", label: "按热度" },
];

export default function FilmCategory() {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const validType = (["movie", "tv", "variety"] as FilmType[]).includes(
    type as FilmType,
  )
    ? (type as FilmType)
    : null;

  const [regions, setRegions] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [heats, setHeats] = useState<string[]>([]);
  const [sort, setSort] = useState("time");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const typeLabel = validType ? typeLabels[validType] : "";

  useSeo(
    validType
      ? `${typeLabel} | 葵司影视资讯`
      : "页面未找到 | 葵司影视资讯",
    validType
      ? `葵司${typeLabel}频道收录最新最全的${typeLabel}作品，支持按地区、年份、热门程度筛选与排序，帮助你快速找到想看的${typeLabel}。`
      : "抱歉，未找到对应的影视分类页面。",
    validType
      ? `葵司,${typeLabel},${typeLabel}列表,${typeLabel}推荐,影视资讯`
      : "葵司,影视资讯",
  );

  const currentPage = Math.max(
    1,
    Number.parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );

  const toggle = (
    list: string[],
    setList: (v: string[]) => void,
    value: string,
  ) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("page");
      return next;
    });
  };

  const handleSort = (value: string) => {
    setSort(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("page");
      return next;
    });
  };

  const resetAll = () => {
    setRegions([]);
    setYears([]);
    setHeats([]);
    setSort("time");
    setSearchParams({});
  };

  const source = validType ? getFilmsByType(validType) : [];

  const filtered = useMemo(() => {
    let list = [...source];
    if (regions.length) list = list.filter((f) => regions.includes(f.region));
    if (years.length) list = list.filter((f) => years.includes(String(f.year)));
    if (heats.length) {
      list = list.filter((f) =>
        heats.some((h) =>
          h === "hot" ? f.popularity >= 88 : f.rating >= 8.2,
        ),
      );
    }
    list.sort((a, b) =>
      sort === "popularity"
        ? b.popularity - a.popularity
        : b.year - a.year || b.popularity - a.popularity,
    );
    return list;
  }, [source, regions, years, heats, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete("page");
      else next.set("page", String(page));
      return next;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!validType) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-20">
        <EmptyState
          icon="ri-error-warning-line"
          title="未找到该影视分类"
          description="你访问的分类不存在，可以返回首页重新选择。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const regionOptions = allRegions.map((r) => ({ value: r, label: r }));
  const yearOptions = allYears.map((y) => ({ value: String(y), label: `${y}年` }));

  const filterContent = (
    <div className="flex flex-col gap-5">
      <FilterGroup
        label="地区"
        options={regionOptions}
        selected={regions}
        onToggle={(v) => toggle(regions, setRegions, v)}
      />
      <FilterGroup
        label="年份"
        options={yearOptions}
        selected={years}
        onToggle={(v) => toggle(years, setYears, v)}
      />
      <FilterGroup
        label="热门程度"
        options={heatOptions}
        selected={heats}
        onToggle={(v) => toggle(heats, setHeats, v)}
      />
    </div>
  );

  return (
    <div>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: typeLabel }]}
          />
          <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl text-foreground-950">
            {typeLabel}
          </h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground-600">
            {typeDescriptions[validType]}
          </p>
        </div>
      </div>

      <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="hidden lg:block rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between gap-4 mb-5">
            <span className="font-heading font-bold text-foreground-950">
              筛选条件
            </span>
            <button
              type="button"
              onClick={resetAll}
              className="text-sm text-foreground-500 hover:text-accent-600 cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
          {filterContent}
        </div>

        <div className="lg:hidden flex items-center justify-between gap-3 mb-4">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="h-10 px-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 cursor-pointer"
          >
            <i className="ri-filter-3-line" />
            筛选
            {regions.length + years.length + heats.length > 0 && (
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-background-950 text-xs">
                {regions.length + years.length + heats.length}
              </span>
            )}
          </button>
          <select
            value={sort}
            onChange={(e) => handleSort(e.target.value)}
            aria-label="排序方式"
            className="h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 cursor-pointer"
          >
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="hidden lg:flex items-center justify-between gap-4 mt-5 mb-6">
          <p className="text-sm text-foreground-500">
            共找到 <span className="text-accent-600 font-medium">{filtered.length}</span> 部{typeLabel}作品
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500">排序：</span>
            {sortOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => handleSort(o.value)}
                className={`px-3 h-8 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  sort === o.value
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-6 lg:mt-0 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {pageItems.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50">
            <EmptyState
              icon="ri-filter-off-line"
              title="没有符合条件的结果"
              description="试试调整或重置筛选条件，也许能找到更多作品。"
              actionLabel="重置筛选"
              onAction={resetAll}
            />
          </div>
        )}
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-background-950/40"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading font-bold text-lg text-foreground-950">
                筛选条件
              </span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="mb-4">
              <span className="text-sm text-foreground-500">排序</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {sortOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => handleSort(o.value)}
                    className={`px-3 h-8 rounded-md text-sm whitespace-nowrap cursor-pointer border transition-colors ${
                      sort === o.value
                        ? "bg-primary-500 text-background-50 border-primary-500"
                        : "bg-background-50 text-foreground-700 border-background-200"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            {filterContent}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={resetAll}
                className="flex-1 h-11 rounded-md border border-background-200 text-sm text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer hover:bg-primary-600 transition-colors"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-content mx-auto px-4 md:px-6 pb-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-accent-600"
        >
          <i className="ri-arrow-left-line" />
          返回首页
        </Link>
      </div>
    </div>
  );
}